export type TimeRange = [number, number];

export interface AvailabilityInput {
  telemetry?: TimeRange[] | null;
  audio?: TimeRange[] | null;
  video?: TimeRange[] | null;
}

export interface AvailabilityBands {
  /** Telemetry that is not covered by any playable medium. */
  telemetry: TimeRange[];
  /** Independently playable Audio not already represented by video. */
  audio: TimeRange[];
  /** Highest verified medium shown by the one scrubber. */
  video: TimeRange[];
  /** Audio union video. */
  playable: TimeRange[];
}

/**
 * Collapse independent readiness into one highest-availability scrubber.
 * Ranges never stack visually: Video wins over Audio, and either playable
 * medium wins over telemetry. The underlying unions remain available for
 * seeking, metrics, and accessible labels.
 */
export function buildAvailabilityBands(input: AvailabilityInput): AvailabilityBands {
  const video = mergeRanges(input.video ?? []);
  const audioTruth = mergeRanges(input.audio ?? []);
  const telemetryTruth = mergeRanges(input.telemetry ?? []);
  const playable = mergeRanges([...audioTruth, ...video]);

  return {
    video,
    audio: subtractRanges(audioTruth, video),
    telemetry: subtractRanges(telemetryTruth, playable),
    playable,
  };
}

export function mergeRanges(ranges: TimeRange[]): TimeRange[] {
  const ordered = ranges
    .filter(
      ([start, end]) =>
        Number.isFinite(start) && Number.isFinite(end) && end > start,
    )
    .map(([start, end]) => [start, end] as TimeRange)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const out: TimeRange[] = [];
  for (const [start, end] of ordered) {
    const previous = out[out.length - 1];
    if (!previous || start > previous[1]) {
      out.push([start, end]);
    } else {
      previous[1] = Math.max(previous[1], end);
    }
  }
  return out;
}

export type MediaStateKind = 'video' | 'audio' | 'gap';

/**
 * The persistent player's badge truth at one UTC instant: full video, LIVE SOUND
 * (independently playable Audio with no video yet), or a gap in both media. Pure so the
 * A5 no-remount contract is deterministically testable — the badge flips, the element never does.
 */
export function mediaStateAt(utcMs: number, bands: AvailabilityBands): MediaStateKind {
  if (rangesContain(bands.video, utcMs)) return 'video';
  if (rangesContain(bands.audio, utcMs)) return 'audio';
  return 'gap';
}

export function rangesContain(ranges: TimeRange[], utcMs: number): boolean {
  for (const [start, end] of ranges) {
    if (utcMs < start) return false; // sorted, non-overlapping
    if (utcMs < end) return true;
  }
  return false;
}

export function subtractRanges(source: TimeRange[], masks: TimeRange[]): TimeRange[] {
  const sourceRanges = mergeRanges(source);
  const maskRanges = mergeRanges(masks);
  const out: TimeRange[] = [];
  for (const [sourceStart, sourceEnd] of sourceRanges) {
    let cursor = sourceStart;
    for (const [maskStart, maskEnd] of maskRanges) {
      if (maskEnd <= cursor) continue;
      if (maskStart >= sourceEnd) break;
      if (maskStart > cursor) out.push([cursor, Math.min(maskStart, sourceEnd)]);
      cursor = Math.max(cursor, maskEnd);
      if (cursor >= sourceEnd) break;
    }
    if (cursor < sourceEnd) out.push([cursor, sourceEnd]);
  }
  return out;
}
